import * as XLSX from 'xlsx'
import { getArticleCode } from './articleCode'
import {
  getPurchaseCatalogCostPrice,
  getPurchaseCatalogMrpPrice,
} from './purchaseCatalogPricing'
import { getLineRemainingToShip } from './shipmentAvailabilityWarning'

/** Lazy load to keep unit tests free of apiClient/axios ESM issues. */
function getPurchaseCatalogService() {
  // eslint-disable-next-line global-require
  return require('../services').purchaseCatalogService
}

export const ARTICLE_CODE_HEADER = 'Article Code'
export const QTY_HEADER = 'Qty'

const ARTICLE_HEADER_ALIASES = new Set([
  'article code',
  'article_code',
  'articlecode',
  'sku',
])

const QTY_HEADER_ALIASES = new Set([
  'qty',
  'quantity',
  'ship qty',
  'ship_qty',
  'ship quantity',
])

/**
 * Normalize a header cell for flexible column matching.
 * @param {unknown} header
 * @returns {string}
 */
export function normalizeShipmentExcelHeader(header) {
  return String(header ?? '')
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
}

/**
 * Normalize article codes for matching (trim + case-insensitive).
 * @param {unknown} code
 * @returns {string}
 */
export function normalizeArticleCodeKey(code) {
  return String(code ?? '')
    .trim()
    .toLowerCase()
}

/**
 * Parse a quantity cell. Returns { ok, value, error }.
 * Non-negative integers only (0 is allowed). Rejects empty, non-numeric,
 * negative, and fractional values.
 * @param {unknown} raw
 * @returns {{ ok: true, value: number } | { ok: false, error: string }}
 */
export function parseShipmentExcelQty(raw) {
  if (raw === null || raw === undefined) {
    return { ok: false, error: 'Quantity is required' }
  }
  const text = String(raw).trim()
  if (!text) {
    return { ok: false, error: 'Quantity is required' }
  }
  // Accept integers and Excel-style whole numbers like "5.0" or "0.0"
  if (!/^-?\d+(?:\.0+)?$/.test(text)) {
    return {
      ok: false,
      error: 'Quantity must be a whole number (fractions are not allowed)',
    }
  }
  const value = Number(text)
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    return { ok: false, error: 'Quantity must be a whole number' }
  }
  if (value < 0) {
    return { ok: false, error: 'Quantity cannot be negative' }
  }
  return { ok: true, value }
}

/**
 * Resolve Article Code / Qty column keys from a sheet_to_json row object.
 * @param {Record<string, unknown>} sampleRow
 * @returns {{ articleKey: string | null, qtyKey: string | null, headers: string[] }}
 */
export function resolveShipmentExcelColumns(sampleRow) {
  const headers = Object.keys(sampleRow || {})
  let articleKey = null
  let qtyKey = null

  for (const header of headers) {
    const normalized = normalizeShipmentExcelHeader(header)
    if (!articleKey && ARTICLE_HEADER_ALIASES.has(normalized)) {
      articleKey = header
    }
    if (!qtyKey && QTY_HEADER_ALIASES.has(normalized)) {
      qtyKey = header
    }
  }

  return { articleKey, qtyKey, headers }
}

/**
 * Build a map of normalized article code → order line item(s).
 * @param {Array<object>} orderLineItems
 * @returns {Map<string, object[]>}
 */
export function buildOrderArticleCodeIndex(orderLineItems) {
  const index = new Map()
  for (const li of orderLineItems || []) {
    const code = getArticleCode(li)
    if (!code) continue
    const key = normalizeArticleCodeKey(code)
    const list = index.get(key) || []
    list.push(li)
    index.set(key, list)
  }
  return index
}

/**
 * Read the first sheet of an Excel ArrayBuffer into row objects.
 * @param {ArrayBuffer} arrayBuffer
 * @returns {{ rows: Record<string, unknown>[], sheetName: string }}
 */
export function readShipmentExcelRows(arrayBuffer) {
  if (arrayBuffer == null) {
    throw new Error('The Excel file could not be read.')
  }

  // FileReader gives ArrayBuffer; tests / Node may give Uint8Array or Buffer.
  let data
  if (typeof Buffer !== 'undefined' && Buffer.isBuffer?.(arrayBuffer)) {
    data = new Uint8Array(arrayBuffer)
  } else if (ArrayBuffer.isView(arrayBuffer)) {
    data = new Uint8Array(
      arrayBuffer.buffer,
      arrayBuffer.byteOffset,
      arrayBuffer.byteLength,
    )
  } else if (arrayBuffer instanceof ArrayBuffer) {
    data = new Uint8Array(arrayBuffer)
  } else {
    throw new Error('The Excel file could not be read.')
  }

  const workbook = XLSX.read(data, { type: 'array', cellDates: false })
  if (!workbook.SheetNames?.length) {
    throw new Error('The Excel file has no sheets.')
  }
  const sheetName = workbook.SheetNames[0]
  const sheet = workbook.Sheets[sheetName]
  if (!sheet) {
    throw new Error('The Excel file has no readable sheet data.')
  }

  const rows = XLSX.utils.sheet_to_json(sheet, {
    defval: '',
    raw: false,
    blankrows: false,
  })

  return { rows, sheetName }
}

/**
 * Aggregate duplicate article codes by summing quantities.
 * Compatible with create-shipment (one qty per order_line_item_id).
 * @param {Array<{ articleCode: string, quantity: number, excelRow: number }>} parsedRows
 * @returns {Array<{ articleCode: string, quantity: number, excelRows: number[], sourceRowCount: number }>}
 */
export function aggregateShipmentExcelRows(parsedRows) {
  const byCode = new Map()
  for (const row of parsedRows) {
    const key = normalizeArticleCodeKey(row.articleCode)
    const existing = byCode.get(key)
    if (!existing) {
      byCode.set(key, {
        articleCode: row.articleCode,
        quantity: row.quantity,
        excelRows: [row.excelRow],
        sourceRowCount: 1,
      })
      continue
    }
    existing.quantity += row.quantity
    existing.excelRows.push(row.excelRow)
    existing.sourceRowCount += 1
  }
  return Array.from(byCode.values())
}

/**
 * Find an existing pending catalog-add row by article code / sku.
 * @param {Array<object>} pendingAdds
 * @param {string} articleCode
 * @returns {object | null}
 */
export function findPendingAddByArticleCode(pendingAdds, articleCode) {
  const key = normalizeArticleCodeKey(articleCode)
  if (!key) return null
  for (const add of pendingAdds || []) {
    const code = normalizeArticleCodeKey(add?.sku || getArticleCode(add) || '')
    if (code && code === key) return add
  }
  return null
}

/**
 * Build a pending-add row shaped like CreateShipmentModal.tryAddCatalogProduct.
 * @param {object} product
 * @param {number} quantity
 * @returns {object}
 */
export function buildCatalogPendingAddFromProduct(product, quantity) {
  const id = String(product?.admin_product_id || product?.id || '').trim()
  const sellingPrice = Number(
    product.selling_price_tp ??
      product.unit_cost ??
      getPurchaseCatalogCostPrice(product) ??
      product.price ??
      0,
  )
  const mrpRaw = Number(
    product.mrp ??
      (product.unit_price != null && product.unit_cost != null
        ? product.unit_price
        : getPurchaseCatalogMrpPrice(product)),
  )
  const mrp =
    Number.isFinite(mrpRaw) && mrpRaw > 0
      ? mrpRaw
      : Number.isFinite(sellingPrice)
        ? sellingPrice
        : 0

  const vatRaw = product.vat
  const vatNum =
    vatRaw === null || vatRaw === undefined || vatRaw === ''
      ? null
      : Number(vatRaw)

  return {
    key: `${id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    admin_product_id: id,
    product_name: product.product_name || product.name || 'Product',
    sku: getArticleCode(product) || product.sku || product.article_code || '',
    quantity: String(quantity),
    dsd: product.dsd === true,
    // Catalog search may coerce null→0; VAT review refreshes from product API.
    vat: Number.isFinite(vatNum) ? vatNum : null,
    source: product.source || product.product_source || 'shwapno',
    unit_price: String(Number.isFinite(sellingPrice) ? sellingPrice : 0),
    mrp: String(mrp),
  }
}

/**
 * Look up a catalog product by exact article code (search, then resolve fallback).
 * Injectable deps for tests.
 *
 * @param {string|number} orgId
 * @param {string} articleCode
 * @param {{
 *   searchCatalog?: Function,
 *   resolveSku?: Function,
 * }} [deps]
 * @returns {Promise<{ ok: true, product: object } | { ok: false, error: string }>}
 */
export async function lookupCatalogProductByArticleCode(
  orgId,
  articleCode,
  deps = {},
) {
  const searchCatalog =
    deps.searchCatalog ||
    ((id, opts) => getPurchaseCatalogService().searchCatalog(id, opts))
  const resolveSku =
    deps.resolveSku ||
    ((id, sku, qty) => getPurchaseCatalogService().resolveSku(id, sku, qty))

  const key = normalizeArticleCodeKey(articleCode)
  try {
    const res = await searchCatalog(orgId, {
      q: articleCode,
      page: 1,
      limit: 25,
    })
    const data = Array.isArray(res?.data) ? res.data : []
    const exact = data.filter((p) => {
      const code = normalizeArticleCodeKey(getArticleCode(p) || p.sku || '')
      return code === key
    })
    if (exact.length === 1) {
      return { ok: true, product: exact[0] }
    }
    if (exact.length > 1) {
      return {
        ok: false,
        error: `Article code "${articleCode}" matches multiple catalog products. Add it manually from the catalog search instead.`,
      }
    }
  } catch {
    // Fall through to resolveSku
  }

  try {
    const res = await resolveSku(orgId, articleCode, 1)
    const resolved = res?.data || res
    if (!resolved) {
      return {
        ok: false,
        error: `Article code "${articleCode}" was not found in the catalog.`,
      }
    }
    const blockers = resolved.blockers || []
    if (
      blockers.includes('unknown_sku') ||
      blockers.includes('external_product')
    ) {
      return {
        ok: false,
        error:
          resolved.error_message ||
          `Article code "${articleCode}" was not found in the catalog.`,
      }
    }
    if (blockers.includes('not_purchasable')) {
      return {
        ok: false,
        error:
          resolved.error_message ||
          `Article code "${articleCode}" is not purchasable and cannot be added to this shipment.`,
      }
    }
    if (!resolved.admin_product_id) {
      return {
        ok: false,
        error:
          resolved.error_message ||
          `Article code "${articleCode}" could not be resolved from the catalog.`,
      }
    }
    return {
      ok: true,
      product: {
        admin_product_id: resolved.admin_product_id,
        id: resolved.admin_product_id,
        product_name: resolved.product_name || 'Product',
        sku: resolved.article_code || articleCode,
        article_code: resolved.article_code || articleCode,
        unit_cost: resolved.unit_cost,
        unit_price: resolved.unit_price,
        selling_price_tp: resolved.unit_cost,
        price: resolved.unit_cost,
      },
    }
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.message ||
      `Failed to look up article code "${articleCode}" in the catalog.`
    return {
      ok: false,
      error: Array.isArray(msg) ? msg.join(', ') : String(msg),
    }
  }
}

/**
 * Resolve Excel rows that are not on the order into catalog pending-adds.
 *
 * @param {Array<object>} unresolved
 * @param {{
 *   orgId: string|number|null,
 *   canAddCatalogProduct: boolean,
 *   pendingAdds?: Array<object>,
 *   existingProductIds?: Set<string>,
 *   lookupProduct?: Function,
 * }} options
 */
export async function resolveUnresolvedShipmentExcelArticles(
  unresolved,
  options = {},
) {
  const errors = []
  const warnings = []
  const catalogMatches = []
  const {
    orgId,
    canAddCatalogProduct,
    pendingAdds = [],
    existingProductIds = new Set(),
    lookupProduct = lookupCatalogProductByArticleCode,
  } = options

  if (!unresolved?.length) {
    return { ok: true, errors, warnings, catalogMatches }
  }

  if (!canAddCatalogProduct) {
    for (const entry of unresolved) {
      errors.push({
        articleCode: entry.articleCode,
        excelRow: entry.excelRows?.[0],
        message: `Article code "${entry.articleCode}" was not found on this order. Only Operations Executive, Operations Manager, or Superuser can add catalog products via Excel.`,
      })
    }
    return { ok: false, errors, warnings, catalogMatches }
  }

  if (!orgId) {
    errors.push({
      message:
        'This order is missing an organisation id, so catalog products cannot be resolved from Excel.',
    })
    return { ok: false, errors, warnings, catalogMatches }
  }

  for (const entry of unresolved) {
    if (entry.sourceRowCount > 1) {
      warnings.push({
        articleCode: entry.articleCode,
        message: `Article "${entry.articleCode}" appeared ${entry.sourceRowCount} times; quantities were summed to ${entry.quantity}.`,
      })
    }

    const existingPending = findPendingAddByArticleCode(
      pendingAdds,
      entry.articleCode,
    )
    if (existingPending) {
      const existingQty = Number(existingPending.quantity) || 0
      catalogMatches.push({
        kind: 'pending',
        admin_product_id: existingPending.admin_product_id,
        pendingKey: existingPending.key,
        quantity: entry.quantity,
        articleCode: existingPending.sku || entry.articleCode,
        productName: existingPending.product_name || '',
        shippedQuantity: 0,
        remainingToShip: 0,
        existingDraftQty: existingQty,
        unit_price: existingPending.unit_price,
        mrp: existingPending.mrp,
        fromCatalog: true,
      })
      continue
    }

    const lookedUp = await lookupProduct(orgId, entry.articleCode)
    if (!lookedUp.ok) {
      errors.push({
        articleCode: entry.articleCode,
        excelRow: entry.excelRows?.[0],
        message: lookedUp.error,
      })
      continue
    }

    const product = lookedUp.product
    const id = String(product?.admin_product_id || product?.id || '').trim()
    if (!id) {
      errors.push({
        articleCode: entry.articleCode,
        excelRow: entry.excelRows?.[0],
        message: `Article code "${entry.articleCode}" resolved without a product id.`,
      })
      continue
    }
    if (existingProductIds.has(id)) {
      errors.push({
        articleCode: entry.articleCode,
        excelRow: entry.excelRows?.[0],
        message: `Article code "${entry.articleCode}" is already on this order or already added — use the order line quantity instead.`,
      })
      continue
    }

    const pending = buildCatalogPendingAddFromProduct(product, entry.quantity)
    catalogMatches.push({
      kind: 'catalog_new',
      admin_product_id: pending.admin_product_id,
      quantity: entry.quantity,
      articleCode: pending.sku || entry.articleCode,
      productName: pending.product_name,
      shippedQuantity: 0,
      remainingToShip: 0,
      existingDraftQty: 0,
      unit_price: pending.unit_price,
      mrp: pending.mrp,
      pendingAdd: pending,
      fromCatalog: true,
    })
    existingProductIds.add(id)
  }

  return {
    ok: errors.length === 0,
    errors,
    warnings,
    catalogMatches,
  }
}

/**
 * Parse + validate an Excel shipment upload against the current order draft.
 *
 * Excel Qty means the desired ship quantity for this shipment draft for that
 * article. Omitted articles are left unchanged (not zeroed).
 *
 * When `allowCatalogAdds` is true, articles not on the order are returned in
 * `unresolved` for async catalog lookup (same capability as Add from catalog).
 *
 * @param {ArrayBuffer|Uint8Array} arrayBuffer
 * @param {{
 *   orderLineItems: Array<object>,
 *   qtyByLine?: Record<string|number, string|number>,
 *   pendingAdds?: Array<object>,
 *   allowCatalogAdds?: boolean,
 * }} context
 */
export function parseAndValidateShipmentExcel(arrayBuffer, context) {
  const orderLineItems = context?.orderLineItems || []
  const qtyByLine = context?.qtyByLine || {}
  const pendingAdds = context?.pendingAdds || []
  const allowCatalogAdds = context?.allowCatalogAdds === true
  const errors = []
  const warnings = []

  let rows
  try {
    ;({ rows } = readShipmentExcelRows(arrayBuffer))
  } catch (err) {
    return {
      ok: false,
      errors: [{ message: err?.message || 'The Excel file could not be parsed.' }],
      warnings: [],
      preview: null,
      updates: [],
      catalogAdds: [],
      unresolved: [],
    }
  }

  if (!rows.length) {
    return {
      ok: false,
      errors: [
        {
          message:
            'The Excel file has no data rows. Add Article Code and Qty columns with at least one row.',
        },
      ],
      warnings: [],
      preview: null,
      updates: [],
      catalogAdds: [],
      unresolved: [],
    }
  }

  const { articleKey, qtyKey } = resolveShipmentExcelColumns(rows[0])
  if (!articleKey || !qtyKey) {
    const missing = []
    if (!articleKey) missing.push(`"${ARTICLE_CODE_HEADER}"`)
    if (!qtyKey) missing.push(`"${QTY_HEADER}"`)
    return {
      ok: false,
      errors: [
        {
          message: `Missing required column(s): ${missing.join(' and ')}. The file must include exactly these columns (order does not matter): ${ARTICLE_CODE_HEADER} | ${QTY_HEADER}.`,
        },
      ],
      warnings: [],
      preview: null,
      updates: [],
      catalogAdds: [],
      unresolved: [],
    }
  }

  const articleIndex = buildOrderArticleCodeIndex(orderLineItems)
  const parsedRows = []

  rows.forEach((row, index) => {
    const excelRow = index + 2 // header is row 1
    const rawCode = row[articleKey]
    const articleCode = String(rawCode ?? '').trim()
    const qtyResult = parseShipmentExcelQty(row[qtyKey])

    if (!articleCode) {
      errors.push({
        excelRow,
        message: `Row ${excelRow}: Article Code is missing.`,
      })
      return
    }

    if (!qtyResult.ok) {
      errors.push({
        excelRow,
        articleCode,
        message: `Row ${excelRow} (${articleCode}): ${qtyResult.error}.`,
      })
      return
    }

    parsedRows.push({
      articleCode,
      quantity: qtyResult.value,
      excelRow,
    })
  })

  if (errors.length) {
    return {
      ok: false,
      errors,
      warnings,
      preview: null,
      updates: [],
      catalogAdds: [],
      unresolved: [],
    }
  }

  const aggregated = aggregateShipmentExcelRows(parsedRows)
  const matched = []
  const unresolved = []
  const catalogMatchesFromPending = []

  for (const entry of aggregated) {
    const key = normalizeArticleCodeKey(entry.articleCode)
    const matches = articleIndex.get(key) || []

    if (matches.length > 1) {
      errors.push({
        articleCode: entry.articleCode,
        excelRow: entry.excelRows[0],
        message: `Article code "${entry.articleCode}" matches multiple order lines (${matches.map((m) => m.id).join(', ')}). Resolve the duplicate lines on the order before importing.`,
      })
      continue
    }

    if (matches.length === 1) {
      const lineItem = matches[0]
      const ordered = Number(lineItem.quantity) || 0
      const remaining = getLineRemainingToShip(lineItem)
      // Existing order articles cannot ship more than remaining (ordered − shipped).
      if (entry.quantity > remaining) {
        errors.push({
          articleCode: entry.articleCode,
          excelRow: entry.excelRows[0],
          message: `Article "${entry.articleCode}": qty ${entry.quantity} exceeds remaining to ship (${remaining}; ordered ${ordered}, already shipped ${Number(lineItem.shipped_quantity) || 0}). Reduce the Excel qty.`,
        })
        continue
      }

      if (entry.sourceRowCount > 1) {
        warnings.push({
          articleCode: entry.articleCode,
          message: `Article "${entry.articleCode}" appeared ${entry.sourceRowCount} times; quantities were summed to ${entry.quantity}.`,
        })
      }

      const existingQty = Number(qtyByLine[lineItem.id]) || 0
      matched.push({
        orderLineItemId: lineItem.id,
        quantity: entry.quantity,
        articleCode: getArticleCode(lineItem) || entry.articleCode,
        productName:
          lineItem.product_name ||
          lineItem.product?.product_name ||
          lineItem.name ||
          '',
        shippedQuantity: Number(lineItem.shipped_quantity) || 0,
        orderedQuantity: ordered,
        remainingToShip: remaining,
        existingDraftQty: existingQty,
        excelRows: entry.excelRows,
        sourceRowCount: entry.sourceRowCount,
        lineItem,
      })
      continue
    }

    // Not on order — maybe already a pending catalog add in the draft.
    // Only when catalog-add permission is enabled (same gate as Add from catalog).
    if (allowCatalogAdds) {
      const existingPending = findPendingAddByArticleCode(
        pendingAdds,
        entry.articleCode,
      )
      if (existingPending) {
        if (entry.sourceRowCount > 1) {
          warnings.push({
            articleCode: entry.articleCode,
            message: `Article "${entry.articleCode}" appeared ${entry.sourceRowCount} times; quantities were summed to ${entry.quantity}.`,
          })
        }
        catalogMatchesFromPending.push({
          kind: 'pending',
          admin_product_id: existingPending.admin_product_id,
          pendingKey: existingPending.key,
          quantity: entry.quantity,
          articleCode: existingPending.sku || entry.articleCode,
          productName: existingPending.product_name || '',
          shippedQuantity: 0,
          remainingToShip: 0,
          existingDraftQty: Number(existingPending.quantity) || 0,
          unit_price: existingPending.unit_price,
          mrp: existingPending.mrp,
          fromCatalog: true,
        })
        continue
      }

      unresolved.push(entry)
      continue
    }

    errors.push({
      articleCode: entry.articleCode,
      excelRow: entry.excelRows[0],
      message: `Article code "${entry.articleCode}" was not found on this order. Only articles belonging to the current order can be shipped.`,
    })
  }

  if (errors.length) {
    return {
      ok: false,
      errors,
      warnings,
      preview: null,
      updates: [],
      catalogAdds: [],
      unresolved: [],
    }
  }

  // Sync path without unresolved catalog lookups
  if (!unresolved.length) {
    const preview = buildShipmentExcelPreview(matched, catalogMatchesFromPending)
    const updates = matched.map((m) => ({
      orderLineItemId: m.orderLineItemId,
      quantity: m.quantity,
      articleCode: m.articleCode,
    }))
    const catalogAdds = catalogMatchesFromPending.map((m) => ({
      kind: m.kind,
      admin_product_id: m.admin_product_id,
      pendingKey: m.pendingKey,
      quantity: m.quantity,
      articleCode: m.articleCode,
      productName: m.productName,
      unit_price: m.unit_price,
      mrp: m.mrp,
      pendingAdd: m.pendingAdd,
    }))

    return {
      ok: true,
      errors: [],
      warnings,
      preview,
      updates,
      catalogAdds,
      unresolved: [],
    }
  }

  return {
    ok: true,
    errors: [],
    warnings,
    preview: null,
    updates: matched.map((m) => ({
      orderLineItemId: m.orderLineItemId,
      quantity: m.quantity,
      articleCode: m.articleCode,
    })),
    matched,
    catalogAdds: catalogMatchesFromPending,
    unresolved,
  }
}

/**
 * Full Excel import including optional catalog resolution for new products.
 *
 * @param {ArrayBuffer|Uint8Array} arrayBuffer
 * @param {{
 *   orderLineItems: Array<object>,
 *   qtyByLine?: Record<string|number, string|number>,
 *   pendingAdds?: Array<object>,
 *   orgId?: string|number|null,
 *   canAddCatalogProduct?: boolean,
 *   existingProductIds?: Set<string>,
 *   lookupProduct?: Function,
 * }} context
 */
export async function parseAndValidateShipmentExcelWithCatalog(
  arrayBuffer,
  context = {},
) {
  const canAdd = context.canAddCatalogProduct === true
  const parsed = parseAndValidateShipmentExcel(arrayBuffer, {
    orderLineItems: context.orderLineItems,
    qtyByLine: context.qtyByLine,
    pendingAdds: context.pendingAdds,
    allowCatalogAdds: canAdd,
  })

  if (!parsed.ok) {
    return {
      ...parsed,
      catalogAdds: parsed.catalogAdds || [],
    }
  }

  if (!parsed.unresolved?.length) {
    return parsed
  }

  const existingIds =
    context.existingProductIds instanceof Set
      ? new Set(context.existingProductIds)
      : new Set(context.existingProductIds || [])

  const resolved = await resolveUnresolvedShipmentExcelArticles(
    parsed.unresolved,
    {
      orgId: context.orgId,
      canAddCatalogProduct: canAdd,
      pendingAdds: context.pendingAdds || [],
      existingProductIds: existingIds,
      lookupProduct: context.lookupProduct,
    },
  )

  const warnings = [...(parsed.warnings || []), ...(resolved.warnings || [])]
  if (!resolved.ok) {
    return {
      ok: false,
      errors: resolved.errors,
      warnings,
      preview: null,
      updates: [],
      catalogAdds: [],
      unresolved: parsed.unresolved,
    }
  }

  const matched = parsed.matched || []
  const catalogMatches = [
    ...(parsed.catalogAdds || []),
    ...resolved.catalogMatches,
  ]
  const preview = buildShipmentExcelPreview(matched, catalogMatches)
  const updates = matched.map((m) => ({
    orderLineItemId: m.orderLineItemId,
    quantity: m.quantity,
    articleCode: m.articleCode,
  }))
  const catalogAdds = catalogMatches.map((m) => ({
    kind: m.kind,
    admin_product_id: m.admin_product_id,
    pendingKey: m.pendingKey,
    quantity: m.quantity,
    articleCode: m.articleCode,
    productName: m.productName,
    unit_price: m.unit_price,
    mrp: m.mrp,
    pendingAdd: m.pendingAdd,
  }))

  return {
    ok: true,
    errors: [],
    warnings,
    preview,
    updates,
    catalogAdds,
    unresolved: [],
  }
}

/**
 * Build preview sections comparing Excel qty to the current shipment draft.
 * Unchanged articles are omitted from section lists (counted in summary only).
 *
 * @param {Array<object>} matched
 * @param {Array<object>} [catalogMatches]
 */
export function buildShipmentExcelPreview(matched, catalogMatches = []) {
  const newArticles = []
  const newCatalogArticles = []
  const increased = []
  const decreased = []
  const unchanged = []
  let totalQtyAdded = 0
  let totalQtyRemoved = 0

  for (const row of matched || []) {
    const existing = Number(row.existingDraftQty) || 0
    const next = Number(row.quantity) || 0
    const delta = next - existing
    const base = {
      orderLineItemId: row.orderLineItemId,
      articleCode: row.articleCode,
      productName: row.productName,
      shippedQuantity: row.shippedQuantity,
      remainingToShip: row.remainingToShip,
      existingQty: existing,
      newQty: next,
      delta,
      quantityToAdd: existing === 0 ? next : Math.max(0, delta),
      fromCatalog: false,
    }

    if (existing <= 0 && next > 0) {
      newArticles.push(base)
      totalQtyAdded += next
    } else if (delta > 0) {
      increased.push(base)
      totalQtyAdded += delta
    } else if (delta < 0) {
      decreased.push(base)
      totalQtyRemoved += Math.abs(delta)
    } else {
      unchanged.push(base)
    }
  }

  for (const row of catalogMatches || []) {
    const existing = Number(row.existingDraftQty) || 0
    const next = Number(row.quantity) || 0
    const delta = next - existing
    const base = {
      orderLineItemId: row.pendingKey || row.admin_product_id,
      admin_product_id: row.admin_product_id,
      articleCode: row.articleCode,
      productName: row.productName,
      shippedQuantity: 0,
      remainingToShip: 0,
      existingQty: existing,
      newQty: next,
      delta,
      quantityToAdd: existing === 0 ? next : Math.max(0, delta),
      fromCatalog: true,
    }

    if (existing <= 0 && next > 0) {
      newCatalogArticles.push(base)
      totalQtyAdded += next
    } else if (delta > 0) {
      increased.push(base)
      totalQtyAdded += delta
    } else if (delta < 0) {
      decreased.push(base)
      totalQtyRemoved += Math.abs(delta)
    } else {
      unchanged.push(base)
    }
  }

  return {
    newArticles,
    newCatalogArticles,
    increased,
    decreased,
    unchanged,
    summary: {
      newArticleCount: newArticles.length + newCatalogArticles.length,
      newOrderArticleCount: newArticles.length,
      newCatalogArticleCount: newCatalogArticles.length,
      increasedCount: increased.length,
      decreasedCount: decreased.length,
      unchangedCount: unchanged.length,
      totalQtyAdded,
      totalQtyRemoved,
      totalArticlesInFile: (matched?.length || 0) + (catalogMatches?.length || 0),
    },
  }
}

/**
 * Apply Excel updates onto a qtyByLine map without zeroing omitted lines.
 * Prefer {@link applyShipmentExcelUpdatesExclusive} for the Excel create flow.
 * @param {Record<string|number, string|number>} qtyByLine
 * @param {Array<{ orderLineItemId: number, quantity: number }>} updates
 * @returns {Record<string|number, string>}
 */
export function applyShipmentExcelUpdates(qtyByLine, updates) {
  const next = { ...qtyByLine }
  for (const update of updates || []) {
    if (update?.orderLineItemId == null) continue
    next[update.orderLineItemId] = String(update.quantity)
  }
  return next
}

/**
 * Build ship qtys for an Excel-only shipment: every order line starts at 0,
 * then only Excel-listed lines get their uploaded quantities.
 * Omitted lines are intentionally not shipped in this flow.
 *
 * @param {Array<{ id: number|string }>} orderLineItems
 * @param {Array<{ orderLineItemId: number, quantity: number }>} updates
 * @returns {Record<string|number, string>}
 */
export function applyShipmentExcelUpdatesExclusive(orderLineItems, updates) {
  const next = {}
  for (const li of orderLineItems || []) {
    if (li?.id == null) continue
    next[li.id] = '0'
  }
  for (const update of updates || []) {
    if (update?.orderLineItemId == null) continue
    next[update.orderLineItemId] = String(update.quantity)
  }
  return next
}

/**
 * Merge Excel catalog adds into the pending-adds draft.
 * Updates qty for existing pending rows; prepends brand-new catalog products.
 *
 * @param {Array<object>} pendingAdds
 * @param {Array<object>} catalogAdds
 * @returns {Array<object>}
 */
export function applyShipmentExcelCatalogAdds(pendingAdds, catalogAdds) {
  let next = [...(pendingAdds || [])]
  for (const add of catalogAdds || []) {
    if (add.kind === 'pending' && add.pendingKey) {
      next = next.map((row) =>
        row.key === add.pendingKey
          ? { ...row, quantity: String(add.quantity) }
          : row,
      )
      continue
    }
    if (add.pendingAdd) {
      const id = String(add.pendingAdd.admin_product_id || '')
      const already = next.some(
        (row) => String(row.admin_product_id || '') === id,
      )
      if (already) {
        next = next.map((row) =>
          String(row.admin_product_id || '') === id
            ? { ...row, quantity: String(add.quantity) }
            : row,
        )
      } else {
        next = [add.pendingAdd, ...next]
      }
    }
  }
  return next
}

/**
 * Pending catalog products for an Excel-only shipment: only rows from the file.
 *
 * @param {Array<object>} catalogAdds
 * @returns {Array<object>}
 */
export function buildExcelOnlyPendingAdds(catalogAdds) {
  const next = []
  for (const add of catalogAdds || []) {
    if (add.pendingAdd) {
      next.push({
        ...add.pendingAdd,
        quantity: String(add.quantity),
      })
      continue
    }
    if (add.kind === 'pending' || add.admin_product_id) {
      next.push({
        key: add.pendingKey || `${add.admin_product_id}-${Date.now()}`,
        admin_product_id: add.admin_product_id,
        product_name: add.productName || 'Product',
        sku: add.articleCode || '',
        quantity: String(add.quantity),
        unit_price: String(add.unit_price ?? '0'),
        mrp: String(add.mrp ?? '0'),
      })
    }
  }
  return next
}
